import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import { compose } from 'redux';
import { Route } from 'react-router-dom';

import {
  Button,
  MaterialIcon,
} from '@pearsontechnology/aero-components';

import {
  PageWrapper,
  PageViewport,
  PageContent,
  PageActions,
  PageActionsRight,
} from 'components/PageStyles';
import helpContext from 'constants/helpContext';
import { ActionTypes } from 'constants/enums';
import enhanceWithServices from 'hocs/enhanceWithServices';
import ProjectActivityList from 'Workspace/Projects/components/ProjectActivityList';
import Sidebar from 'Workspace/components/WorkspaceSidebar';
import HomeNavigation from 'Workspace/components/HomeNavigation';
import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import WorkspaceHeader from 'Workspace/components/WorkspaceHeader';
import HelpMenu from 'Workspace/components/WorkspaceHelpMenu';
import EmptyPageBuilder from 'components/EmptyPage';
import classEmptyDarkImage from 'images/class-empty-dark.png';
import classEmptyLightImage from 'images/class-empty-light.png';
import {
  Grid,
  GridCell,
} from 'components/LayoutGrid';
import {
  commandWorkspaceProjectList,
} from 'containers/ConnectionManager/actions';
import PlaceholderCardList from 'components/PlaceholderCardList';
import TabHeader from 'components/TabHeader';
import gtmService from 'utils/gtm/gtmService';
import projectServiceShape from 'services/types/projectService';
import workspaceService from 'services/WorkspaceService';
import { selectWorkspacesList } from 'Workspace/Admin/store/selectors';
import { featureKeys } from 'services/FeatureFlagService';
import messages from './messages';
import { PlaceholderPageContent, ProjectRoute } from './styles';
import { selectProjectList } from '../../store/selectors';
import ProjectCard from '../ProjectCard';
import CreateProjectModal from '../CreateProjectModal';
import DeleteConfirm from '../DeleteConfirm';
import MoveProjectModal from '../MoveProjectModal';
import enhanceWithFeatureFlagSubscription from 'hocs/enhanceWithFeatureFlagSubscription';

const EmptyPage = EmptyPageBuilder.build({
  lightBackgroundImage: classEmptyLightImage,
  darkBackgroundImage: classEmptyDarkImage,
});

const ProjectsList = ({ getProjectsList, projects, projectService, workspaceList, getFeatureFlagValue}) => {
  const [isCreateProjectModalVisible, setCreateProjectModalVisible] = useState(false);
  const [isMoveProjectModalVisible, setMoveProjectModalVisible] = useState(false);
  const [isDeleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [activeProjectId, setActiveProjectId] = useState();
  const selectedWorkspace = workspaceService.getSelectedWorkspace();
  const isTabHeaderVisible = getFeatureFlagValue(featureKeys.BRNT_18710_WORKSPACE_REDESIGN_22);

  useEffect(() => {
    if (location) {
      const { pathname } = location;
      gtmService.pushTelemetryPageActivityAndView({ pageName: pathname });
    }
  }, []);

  useEffect(() => {
    if (!isSelectedWorkspaceExist() && workspaceList) {
      workspaceService.setSelectedWorkspace(workspaceList[0]);
    }
    if (isSelectedWorkspaceExist()) {
      getProjectsList(selectedWorkspace.id);
    }
  }, [workspaceList, getProjectsList, selectedWorkspace.id]);
  
  const isSelectedWorkspaceExist = () => Object.keys(workspaceService.getSelectedWorkspace()).length !== 0;
  
  const handleActionMenuItemClick = (actionType, projectId) => {
    setActiveProjectId(projectId);
    switch (actionType) {
      case ActionTypes.MOVE:
        openMoveProjectModal();
        break;
      case ActionTypes.DELETE:
        openDeleteConfirm();
        break;
      default:
    }
  };

  const openCreateProjectModal = () => {
    setCreateProjectModalVisible(true);
  };

  const closeCreateProjectModal = () => {
    setCreateProjectModalVisible(false);
  };

  const openMoveProjectModal = () => {
    setMoveProjectModalVisible(true);
  };

  const closeMoveProjectModal = () => {
    setMoveProjectModalVisible(false);
  };

  const openDeleteConfirm = () => {
    setDeleteConfirmVisible(true);
  };

  const closeDeleteConfirm = () => {
    setDeleteConfirmVisible(false);
  };

  const handleMoveProject = async (workspaceId) => {
    await projectService.moveProject(activeProjectId, workspaceId);
    closeMoveProjectModal();
    getProjectsList();
  };

  const handleDeleteProject = async () => {
    await projectService.deleteProject(activeProjectId);
    closeDeleteConfirm();
    getProjectsList();
  };

  const getContent = () => {
    if (typeof projects === 'undefined') {
      return (
        <PlaceholderPageContent>
          <PlaceholderCardList />
        </PlaceholderPageContent>
      );
    }

    return projects.length
      ? (
        <Fragment>
          {isTabHeaderVisible && <TabHeader></TabHeader>}
          <PageActions>
            <PageActionsRight>
              <Button primary onClick={openCreateProjectModal}>
                <MaterialIcon id="add" left />
                {intl.formatMessage(messages.newProject)}
              </Button>
            </PageActionsRight>
          </PageActions>
          <PageContent>
            <Grid>
              {
                projects
                  .sort((a, b) => (a.name).localeCompare(b.name))
                  .map((project) => (
                    <GridCell key={`${project.id}cell`} span={{ desktop: 4, tablet: 6, mobile: 12 }} >
                      <ProjectRoute to={`/courseware/${project.id}`}>
                        <ProjectCard
                          permissionLevel={project.permissionLevel}
                          key={project.id}
                          projectId={project.id}
                          name={project.name}
                          createdAt={project.createdAt}
                          onActionsMenuItemClick={handleActionMenuItemClick}
                        />
                      </ProjectRoute>
                      <Route path="/courseware/:projectId" component={ProjectActivityList} />
                    </GridCell>
                  ))
              }
            </Grid>
          </PageContent>
        </Fragment>
      ) : (
        <PageContent>
          <EmptyPage
            title={intl.formatMessage(messages.emptyPageTitle)}
            description={intl.formatMessage(messages.emptyPageDescription)}
            linkUrl="#"
            linkText={intl.formatMessage(messages.emptyPageLinkText)}
            buttonText={intl.formatMessage(messages.emptyPageButtonText)}
            buttonCallback={openCreateProjectModal}
          />
        </PageContent>
      );
  };

  return (
    <PageWrapper>
      <Helmet
        title={intl.formatMessage(messages.title)}
        description={intl.formatMessage(messages.description)}
      />
      <Sidebar navigation={<HomeNavigation selectedKey="courseware" />} />
      <PageViewport>
        {isTabHeaderVisible ? <WorkspaceHeader title={selectedWorkspace && selectedWorkspace.name} />
          : <WorkspaceHeader title={intl.formatMessage(messages.title)} />}
        {getContent()}
      </PageViewport>
      <HelpMenu helpContext={helpContext.PROJECTS} />
      <CreateProjectModal visible={isCreateProjectModalVisible} onDismiss={closeCreateProjectModal} />
      <MoveProjectModal visible={isMoveProjectModalVisible} onDismiss={closeMoveProjectModal} handleMoveProject={handleMoveProject} />
      <DeleteConfirm visible={isDeleteConfirmVisible} onDismiss={closeDeleteConfirm} handleDeleteProject={handleDeleteProject} />
    </PageWrapper>
  );
};

ProjectsList.propTypes = {
  getProjectsList: PropTypes.func,
  projects: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object,
  ]),
  projectService: projectServiceShape,
};

const mapStateToProps = (state) => ({
  projects: selectProjectList(state),
  workspaceList: selectWorkspacesList(state),
});

const mapDispatchToProps = (dispatch) => ({
  getProjectsList: () => dispatch(commandWorkspaceProjectList()),
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);
const flagNames = [featureKeys.BRNT_18710_WORKSPACE_REDESIGN_22];
export default compose(withConnect, enhanceWithServices, enhanceWithFeatureFlagSubscription)(ProjectsList, flagNames);
